import { useCallback, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { Card, ChevronRightIcon, ExpandMoreIcon, Flex, Icon, IconButton, Text } from '@tradener/lumen';

import CounterpartyFilters from './components/CounterpartyFilters';
import CounterpartyList from './components/CounterpartyList';
import NineBox from './components/NineBox';
import useOperationLimitsDashboard from './hooks/useOperationLimitsDashboard';
import useOperationLimitsNineBox from './hooks/useOperationLimitsNineBox';
import useOperationLimitsRatingMatrix from './hooks/useOperationLimitsRatingMatrix';

const COUNTERPARTY_COLLAPSIBLE_SECTION_ID = 'counterparty-collapsible-section';

function OperationLimitsDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCounterpartyCollapsed, setIsCounterpartyCollapsed] = useState(false);

  const {
    search,
    hasExposure,
    sortField,
    sortDirection,
    loading,
    loadingMore,
    counterparties,
    hasMore,
    onSearch,
    onToggleHasExposure,
    onSort,
    onLoadMore,
  } = useOperationLimitsDashboard();
  const {
    metric,
    selectedCell,
    selectedRating,
    counterparties: filteredCounterparties,
    onMetricChange,
    onClearFilters,
    onRatingSelect,
    onCellSelect,
  } = useOperationLimitsNineBox({ counterparties });
  const {
    years,
    metricData,
    isLoading: isRatingMatrixLoading,
    hasData: hasRatingMatrixData,
    consideredPeriodLabel,
  } = useOperationLimitsRatingMatrix({
    metric,
  });

  const handleOpenDetail = useCallback(
    (accountId: string, counterpartyName: string) => {
      const searchParams = new URLSearchParams(location.search);

      searchParams.set('counterpartyName', counterpartyName);

      const nextSearch = searchParams.toString();

      navigate(`/limits/dashboard/${accountId}${nextSearch ? `?${nextSearch}` : ''}`);
    },
    [location.search, navigate],
  );

  return (
    <Flex direction="column" width="full" p="5" rowGap="6" data-testid="credit-page">
      <NineBox
        metric={metric}
        selectedCell={selectedCell}
        selectedRating={selectedRating}
        years={years}
        metricData={metricData}
        isLoading={isRatingMatrixLoading}
        hasData={hasRatingMatrixData}
        consideredPeriodLabel={consideredPeriodLabel}
        onMetricChange={onMetricChange}
        onClearFilters={onClearFilters}
        onRatingSelect={onRatingSelect}
        onCellSelect={onCellSelect}
      />

      <Flex direction="column" gap="5">
        <Flex align="center" gap="1.5">
          <IconButton
            size="sm"
            variant="ghost"
            colorScheme="gray"
            aria-label="Alternar colapso da seção de contraparte"
            aria-controls={COUNTERPARTY_COLLAPSIBLE_SECTION_ID}
            aria-expanded={!isCounterpartyCollapsed}
            icon={<Icon as={isCounterpartyCollapsed ? ChevronRightIcon : ExpandMoreIcon} color="gray.500" boxSize="5" />}
            onClick={() => setIsCounterpartyCollapsed((previous) => !previous)}
          />

          <Text textStyle="body1-semibold" color="gray.500" lineHeight="1">
            Exposição por contraparte
          </Text>
        </Flex>

        <Flex
          id={COUNTERPARTY_COLLAPSIBLE_SECTION_ID}
          data-testid={COUNTERPARTY_COLLAPSIBLE_SECTION_ID}
          display={isCounterpartyCollapsed ? 'none' : 'flex'}
          aria-hidden={isCounterpartyCollapsed}
        >
          <Card width="full" borderRadius="xl" p="5">
            <Flex direction="column" gap="6" width="full">
              <CounterpartyFilters
                search={search}
                hasExposure={hasExposure}
                onSearch={onSearch}
                onToggleHasExposure={onToggleHasExposure}
              />

              <CounterpartyList
                counterparties={filteredCounterparties}
                loading={loading}
                loadingMore={loadingMore}
                hasMore={hasMore}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={onSort}
                onLoadMore={onLoadMore}
                onOpenDetail={handleOpenDetail}
              />
            </Flex>
          </Card>
        </Flex>
      </Flex>
    </Flex>
  );
}

export default OperationLimitsDashboard;
